import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { tournamentService } from "../../services/api/tournamentService";
import { getUserSports, getAllSports } from "../../services/api/sportsService";
import { isTeamSport } from "../../config/sportsConfig";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import VenueSearch from "../ui/VenueSearch";
import { eid } from "../../utils/entityId";
import { parseDateOnly } from "../../utils/dateOnly";
import { getMatchFormatLabels } from "../../utils/matchFormatTerms";
import "../../styles/features/tournaments.css";
import { viewerTimeZone } from "../../utils/eventTime";
import { PriceHint } from "../payments/PriceField";
import { RefundPolicyField } from "../payments/RefundPolicy";

const TournamentCreation = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sports, setSports] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showFormatInfo, setShowFormatInfo] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    sport_id: "",
    tournament_type: "single_elimination",
    category: "competitive",
    skill_level: "",
    start_date: "",
    end_date: "",
    registration_start: "",
    registration_end: "",
    max_participants: 16,
    min_participants: 2,
    entry_fee: 0,
    refund_policy: "",
    prize_pool: 0,
    is_public: true,
    requires_approval: false,
    // Venue
    venue: "",
    venue_address: "",
    venue_lat: null,
    venue_lng: null,
    venue_place_id: "",
    court: "",
    // Courts
    num_courts: 1,
    // Match format (for set-based sports like volleyball, badminton, tennis)
    match_format: "best_of_3",
    // Play format for individual sports (singles/doubles)
    play_format: "singles",
    // Groups + knockout layout
    groups_count: 2,
    teams_advancing_per_group: 2,
    // Swiss: number of rounds ("" = automatic, ceil(log2 entries))
    swiss_rounds: "",
    // Create the next bracket matches as soon as results come in
    auto_advance: true,
  });

  // Sports that support multi-set match format selection
  const SETS_BASED_SPORTS = ["volleyball", "badminton", "tennis", "table tennis", "squash", "racquetball"];
  const selectedSportName = (sports.find((s) => String(s.id) === String(formData.sport_id))?.name || "").toLowerCase();
  const isSetsBasedSport = SETS_BASED_SPORTS.some((s) => selectedSportName.includes(s));
  const isIndividualSport = selectedSportName ? !isTeamSport(selectedSportName) : false;
  // "games" for racket sports, "players" for individual sports (BUG-63)
  const matchFormatLabels = getMatchFormatLabels(selectedSportName, {
    isIndividual: isIndividualSport,
    playFormat: formData.play_format,
  });

  // Load sports on component mount — user's own sports first, fallback to all
  useEffect(() => {
    const loadSports = async () => {
      try {
        let sportsData = [];
        try {
          const userRes = await getUserSports();
          sportsData = userRes?.data?.sports || userRes?.sports || userRes?.data || [];
        } catch { sportsData = []; }
        if (!sportsData.length) {
          const allRes = await getAllSports();
          sportsData = allRes?.data || [];
        }
        setSports(sportsData);
      } catch (err) {
        setError("Failed to load sports");
      }
    };
    loadSports();
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle venue change from VenueSearch
  const handleVenueChange = (venueObj) => {
    setFormData((prev) => ({
      ...prev,
      venue: venueObj.venue || "",
      venue_address: venueObj.venue_address || "",
      venue_lat: venueObj.venue_lat ?? null,
      venue_lng: venueObj.venue_lng ?? null,
      venue_place_id: venueObj.venue_place_id || "",
      court: venueObj.court || "",
    }));
  };

  // Validate form data
  const validateForm = () => {
    const errors = [];

    if (!formData.name.trim()) {
      errors.push("Tournament name is required");
    }

    if (!formData.sport_id) {
      errors.push("Sport selection is required");
    }

    if (!formData.venue || !formData.venue.trim()) {
      errors.push("Venue is required — players need to know where to show up");
    }

    if (!formData.start_date) {
      errors.push("Start date is required");
    }

    if (!formData.end_date) {
      errors.push("End date is required");
    }

    if (formData.start_date && parseDateOnly(formData.start_date) <= new Date()) {
      errors.push("Start date must be in the future");
    }

    // A one-day tournament starts and ends on the same date (TN-B14)
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      errors.push("End date cannot be before the start date");
    }

    if (formData.tournament_type === "group_knockout") {
      const groups = parseInt(formData.groups_count) || 0;
      const advancing = parseInt(formData.teams_advancing_per_group) || 0;
      if (groups < 1) errors.push("Number of groups must be at least 1");
      if (advancing < 1) errors.push("At least one participant per group must advance");
      if (groups * advancing < 2) errors.push("The knockout needs at least 2 qualifiers");
      if (groups * 2 > parseInt(formData.max_participants)) {
        errors.push(`${groups} groups need room for at least ${groups * 2} participants`);
      }
    }

    if (
      formData.registration_end &&
      new Date(formData.registration_end) > new Date(formData.start_date)
    ) {
      errors.push(
        "Registration end date cannot be after tournament start date"
      );
    }

    if (
      parseInt(formData.max_participants) < parseInt(formData.min_participants)
    ) {
      errors.push(
        "Maximum participants cannot be less than minimum participants"
      );
    }

    if (parseFloat(formData.entry_fee) < 0) {
      errors.push("Entry fee cannot be negative");
    }

    if (parseFloat(formData.prize_pool) < 0) {
      errors.push("Prize pool cannot be negative");
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(". "));
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Prepare tournament data
      const tournamentData = {
        ...formData,
        sport_id: parseInt(formData.sport_id),
        max_participants: parseInt(formData.max_participants),
        min_participants: parseInt(formData.min_participants),
        entry_fee: parseFloat(formData.entry_fee),
        prize_pool: parseFloat(formData.prize_pool),
        num_courts: parseInt(formData.num_courts) || 1,
        organization_id: user.organization_id || null,
        groups_count: parseInt(formData.groups_count) || 2,
        teams_advancing_per_group: parseInt(formData.teams_advancing_per_group) || 2,
        // Store match format (and Swiss rounds) in bracket_settings so schedule generation picks them up
        bracket_settings: (() => {
          const settings = {};
          if (isSetsBasedSport) settings.match_format = formData.match_format;
          if (formData.tournament_type === "swiss_system" && parseInt(formData.swiss_rounds) > 0) {
            settings.swiss_rounds = parseInt(formData.swiss_rounds);
          }
          if (formData.tournament_type !== "round_robin") settings.auto_advance = !!formData.auto_advance;
          return Object.keys(settings).length ? settings : undefined;
        })(),
      };
      delete tournamentData.swiss_rounds;
      delete tournamentData.auto_advance;
      // Singles/doubles only exists for racket sports; a court belongs to a match, not the tournament (TN-B11)
      if (!isIndividualSport) delete tournamentData.play_format;
      delete tournamentData.court;
      // "Any skill level" is no level (TN-B09)
      if (!tournamentData.skill_level) tournamentData.skill_level = null;
      // Dates are days at the venue; this device's zone only counts without a map pin
      tournamentData.timezone = viewerTimeZone();

      const response = await tournamentService.createTournament(
        tournamentData
      );

      setSuccess("Tournament created successfully!");

      // Redirect to tournament details after a short delay
      // After backend fix: response.data = tournament object directly
      setTimeout(() => {
        navigate(`/tournaments/${eid(response.data) || response.data.id}`);
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      name: "",
      description: "",
      sport_id: "",
      tournament_type: "single_elimination",
      category: "competitive",
      start_date: "",
      end_date: "",
      registration_start: "",
      registration_end: "",
      max_participants: 16,
      min_participants: 2,
      entry_fee: 0,
      refund_policy: "",
      prize_pool: 0,
      is_public: true,
      requires_approval: false,
      venue: "",
      venue_address: "",
      venue_lat: null,
      venue_lng: null,
      venue_place_id: "",
      court: "",
      num_courts: 1,
      match_format: "best_of_3",
      groups_count: 2,
      teams_advancing_per_group: 2,
      swiss_rounds: "",
      auto_advance: true,
    });
    setError("");
    setSuccess("");
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  return (
    <GameLayout onLogout={() => {}} profile={user} showFloatingNav>
      <div className="tournament-creation-gaming">
        <FloatingCard className="header-card-gaming">
          <div className="header-content-gaming">
            <h2 className="tournament-title">⚔️ Create New Tournament</h2>
            <p className="tournament-subtitle">
              Organize a competitive tournament for your sport
            </p>
          </div>
        </FloatingCard>

        {error && (
          <FloatingCard className="alert-card alert-error-gaming">
            <div className="alert-content">
              <span className="alert-icon">❌</span>
              <div className="alert-message">{error}</div>
            </div>
          </FloatingCard>
        )}

        {success && (
          <FloatingCard className="alert-card alert-success-gaming">
            <div className="alert-content">
              <span className="alert-icon">✅</span>
              <div className="alert-message">{success}</div>
            </div>
          </FloatingCard>
        )}

        <form onSubmit={handleSubmit} className="tournament-form-gaming">
          {/* Basic Information */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">📋 Basic Information</h3>

            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label htmlFor="name" className="form-label-gaming">
                  Tournament Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter tournament name"
                  required
                  maxLength={255}
                  className="form-input-gaming"
                />
              </div>

              <div className="form-group-gaming">
                <label htmlFor="sport_id" className="form-label-gaming">
                  Sport *
                </label>
                <select
                  id="sport_id"
                  name="sport_id"
                  value={formData.sport_id}
                  onChange={handleInputChange}
                  required
                  className="form-select-gaming"
                >
                  <option value="">Select a sport</option>
                  {sports.map((sport) => (
                    <option key={sport.id} value={sport.id}>
                      {sport.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group-gaming">
              <label htmlFor="description" className="form-label-gaming">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your tournament..."
                rows={4}
                maxLength={1000}
                className="form-textarea-gaming"
              />
            </div>
          </FloatingCard>

          {/* Tournament Settings */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">⚙️ Tournament Settings</h3>

            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label htmlFor="tournament_type" className="form-label-gaming">
                  Tournament Type{" "}
                  <button
                    type="button"
                    onClick={() => setShowFormatInfo((v) => !v)}
                    style={{ background: "none", border: "none", color: "#a78bfa", cursor: "pointer", fontSize: "0.9rem", padding: "0 0.25rem", verticalAlign: "middle" }}
                    title="Learn about tournament formats"
                  >
                    ℹ️
                  </button>
                </label>
                <select
                  id="tournament_type"
                  name="tournament_type"
                  value={formData.tournament_type}
                  onChange={handleInputChange}
                  className="form-select-gaming"
                >
                  <option value="single_elimination">🏆 Single Elimination</option>
                  <option value="double_elimination">🔄 Double Elimination</option>
                  <option value="round_robin">🔁 Round Robin</option>
                  <option value="swiss_system">♟️ Swiss System</option>
                  <option value="group_knockout">🧩 Groups + Knockout</option>
                </select>

                {showFormatInfo && (
                  <div style={{ marginTop: "0.75rem", padding: "0.85rem 1rem", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: "0.5rem", fontSize: "0.83rem", color: "#94a3b8", lineHeight: 1.6 }}>
                    {{
                      single_elimination:
                        "🏆 Single Elimination — One loss and you're out. Fast, high-drama bracket. Participants are seeded so top seeds meet only in the final. Best for 8–64 teams.",
                      double_elimination:
                        "🔄 Double Elimination — Teams get a second chance via the Losers Bracket. One bad game doesn't end your tournament. Best for competitive leagues with 4–32 teams.",
                      round_robin:
                        "🔁 Round Robin — Everyone plays everyone. The most consistent team wins. Best for 4–12 teams in a festival or league setting.",
                      swiss_system:
                        "♟️ Swiss System — Play a set number of rounds. Each round pairs participants on the same score, never the same opponent twice. No one is eliminated. Best for large tournaments (chess, card games, etc.) where full round-robin isn't practical.",
                      group_knockout:
                        "🧩 Groups + Knockout — Participants are split into groups that each play a round robin. The top finishers of every group go into a seeded knockout bracket. Like the World Cup.",
                    }[formData.tournament_type]}
                  </div>
                )}
              </div>

              <div className="form-group-gaming">
                <label htmlFor="category" className="form-label-gaming">
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="form-select-gaming"
                >
                  <option value="friendly">🤝 Friendly</option>
                  <option value="competitive">⚔️ Competitive</option>
                  <option value="ranked">🏅 Ranked</option>
                </select>
              </div>

              <div className="form-group-gaming">
                <label htmlFor="skill_level" className="form-label-gaming">
                  Skill Level
                </label>
                <select
                  id="skill_level"
                  name="skill_level"
                  value={formData.skill_level || ""}
                  onChange={handleInputChange}
                  className="form-select-gaming"
                >
                  <option value="">Any skill level</option>
                  <option value="beginner">🌿 Beginner</option>
                  <option value="recreational">🌱 Recreational</option>
                  <option value="intermediate">⚡ Intermediate</option>
                  <option value="advanced">🔥 Advanced</option>
                  <option value="professional">👑 Professional</option>
                </select>
              </div>
            </div>

            {/* Groups + Knockout layout */}
            {formData.tournament_type === "group_knockout" && (
              <div className="form-row-gaming">
                <div className="form-group-gaming">
                  <label htmlFor="groups_count" className="form-label-gaming">
                    🧩 Number of Groups
                  </label>
                  <input
                    type="number"
                    id="groups_count"
                    name="groups_count"
                    value={formData.groups_count}
                    onChange={handleInputChange}
                    className="form-input-gaming"
                    min="1"
                    max="32"
                  />
                </div>
                <div className="form-group-gaming">
                  <label htmlFor="teams_advancing_per_group" className="form-label-gaming">
                    ⬆️ Advancing per Group
                  </label>
                  <input
                    type="number"
                    id="teams_advancing_per_group"
                    name="teams_advancing_per_group"
                    value={formData.teams_advancing_per_group}
                    onChange={handleInputChange}
                    className="form-input-gaming"
                    min="1"
                    max="32"
                  />
                  <p style={{ color: "#64748b", fontSize: "0.8rem", marginTop: "0.4rem" }}>
                    {`${(parseInt(formData.groups_count) || 0) * (parseInt(formData.teams_advancing_per_group) || 0)} participants reach the knockout. Groups are filled by seed (snake order).`}
                  </p>
                </div>
              </div>
            )}

            {/* Swiss rounds */}
            {formData.tournament_type === "swiss_system" && (
              <div className="form-row-gaming">
                <div className="form-group-gaming">
                  <label htmlFor="swiss_rounds" className="form-label-gaming">
                    ♟️ Number of Rounds
                  </label>
                  <input
                    type="number"
                    id="swiss_rounds"
                    name="swiss_rounds"
                    value={formData.swiss_rounds}
                    onChange={handleInputChange}
                    className="form-input-gaming"
                    min="1"
                    max="20"
                    placeholder="Automatic"
                  />
                  <p style={{ color: "#64748b", fontSize: "0.8rem", marginTop: "0.4rem" }}>
                    Leave blank for automatic: enough rounds to separate the field (log₂ of the entries, rounded up).
                  </p>
                </div>
              </div>
            )}

            {/* Play Format — only shown for individual sports (badminton, tennis, table tennis) */}
            {isIndividualSport && (
              <div className="form-row-gaming">
                <div className="form-group-gaming">
                  <label htmlFor="play_format" className="form-label-gaming">
                    🏸 Play Format
                  </label>
                  <select
                    id="play_format"
                    name="play_format"
                    value={formData.play_format}
                    onChange={handleInputChange}
                    className="form-select-gaming"
                  >
                    <option value="singles">Singles (1 vs 1)</option>
                    <option value="doubles">👥 Doubles (2 vs 2)</option>
                    <option value="mixed">👫 Mixed (2 vs 2)</option>
                  </select>
                  <p style={{ color: "#64748b", fontSize: "0.8rem", marginTop: "0.4rem" }}>
                    {formData.play_format === "singles"
                      ? "Each match is played between two individual players."
                      : "Each match is played between two pairs of players."}
                  </p>
                </div>
              </div>
            )}

            {/* Match Format — only shown for set-based sports (volleyball, badminton, tennis, etc.) */}
            {isSetsBasedSport && (
              <div className="form-row-gaming">
                <div className="form-group-gaming">
                  <label htmlFor="match_format" className="form-label-gaming">
                    ⚙️ Match Format
                  </label>
                  <select
                    id="match_format"
                    name="match_format"
                    value={formData.match_format}
                    onChange={handleInputChange}
                    className="form-select-gaming"
                  >
                    <option value="best_of_1">{matchFormatLabels.options.best_of_1}</option>
                    <option value="best_of_3">{matchFormatLabels.options.best_of_3}</option>
                    <option value="best_of_5">{matchFormatLabels.options.best_of_5}</option>
                  </select>
                  <p style={{ color: "#64748b", fontSize: "0.8rem", marginTop: "0.4rem" }}>
                    {matchFormatLabels.help[formData.match_format]}
                  </p>
                </div>
              </div>
            )}

            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label htmlFor="max_participants" className="form-label-gaming">
                  Max Participants
                </label>
                <input
                  type="number"
                  id="max_participants"
                  name="max_participants"
                  value={formData.max_participants}
                  onChange={handleInputChange}
                  min="2"
                  max="256"
                  className="form-input-gaming"
                />
              </div>

              <div className="form-group-gaming">
                <label htmlFor="min_participants" className="form-label-gaming">
                  Min Participants
                </label>
                <input
                  type="number"
                  id="min_participants"
                  name="min_participants"
                  value={formData.min_participants}
                  onChange={handleInputChange}
                  min="2"
                  max={formData.max_participants}
                  className="form-input-gaming"
                />
              </div>

              <div className="form-group-gaming">
                <label htmlFor="num_courts" className="form-label-gaming">
                  Number of Courts / Fields
                </label>
                <input
                  type="number"
                  id="num_courts"
                  name="num_courts"
                  value={formData.num_courts}
                  onChange={handleInputChange}
                  min="1"
                  className="form-input-gaming"
                />
              </div>
            </div>
          </FloatingCard>

          {/* Venue */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">📍 Venue *</h3>
            <VenueSearch
              label="Tournament Venue"
              required
              value={{
                venue: formData.venue,
                venue_address: formData.venue_address,
                venue_lat: formData.venue_lat,
                venue_lng: formData.venue_lng,
                venue_place_id: formData.venue_place_id,
                court: formData.court,
              }}
              onChange={handleVenueChange}
            />
          </FloatingCard>

          {/* Dates */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">📅 Tournament Dates</h3>

            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label htmlFor="start_date" className="form-label-gaming">
                  Start Date *
                </label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  min={getMinDate()}
                  required
                  className="form-input-gaming"
                />
              </div>

              <div className="form-group-gaming">
                <label htmlFor="end_date" className="form-label-gaming">
                  End Date *
                </label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  min={formData.start_date || getMinDate()}
                  required
                  className="form-input-gaming"
                />
              </div>
            </div>

            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label
                  htmlFor="registration_start"
                  className="form-label-gaming"
                >
                  Registration Start
                </label>
                <input
                  type="date"
                  id="registration_start"
                  name="registration_start"
                  value={formData.registration_start}
                  onChange={handleInputChange}
                  min={getMinDate()}
                  className="form-input-gaming"
                />
              </div>

              <div className="form-group-gaming">
                <label htmlFor="registration_end" className="form-label-gaming">
                  Registration End
                </label>
                <input
                  type="date"
                  id="registration_end"
                  name="registration_end"
                  value={formData.registration_end}
                  onChange={handleInputChange}
                  min={formData.registration_start || getMinDate()}
                  max={formData.start_date}
                  className="form-input-gaming"
                />
              </div>
            </div>
          </FloatingCard>

          {/* Financial */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">💰 Financial Details</h3>

            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label htmlFor="entry_fee" className="form-label-gaming">
                  Entry Fee ($)
                </label>
                <input
                  type="number"
                  id="entry_fee"
                  name="entry_fee"
                  value={formData.entry_fee}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="form-input-gaming"
                />
                {/* While payments are on, the captain pays this in the app */}
                <PriceHint value={formData.entry_fee} payer="Captains" hint="0 = free entry." />
              </div>
              <RefundPolicyField
                value={formData.refund_policy}
                price={formData.entry_fee}
                onChange={(refund_policy) => setFormData((prev) => ({ ...prev, refund_policy }))}
              />

              <div className="form-group-gaming">
                <label htmlFor="prize_pool" className="form-label-gaming">
                  Prize Pool ($)
                </label>
                <input
                  type="number"
                  id="prize_pool"
                  name="prize_pool"
                  value={formData.prize_pool}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="form-input-gaming"
                />
              </div>
            </div>
          </FloatingCard>

          {/* Settings */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">🔧 Additional Settings</h3>

            <div className="form-row-gaming">
              <div className="form-group-gaming checkbox-group-gaming">
                <label className="checkbox-label-gaming">
                  <input
                    type="checkbox"
                    name="is_public"
                    checked={formData.is_public}
                    onChange={handleInputChange}
                    className="checkbox-input-gaming"
                  />
                  <span className="checkmark-gaming"></span>
                  Public Tournament (visible to all users)
                </label>
              </div>

              <div className="form-group-gaming checkbox-group-gaming">
                <label className="checkbox-label-gaming">
                  <input
                    type="checkbox"
                    name="requires_approval"
                    checked={formData.requires_approval}
                    onChange={handleInputChange}
                    className="checkbox-input-gaming"
                  />
                  <span className="checkmark-gaming"></span>
                  Require approval for registration
                </label>
              </div>

              {formData.tournament_type !== "round_robin" && (
                <div className="form-group-gaming checkbox-group-gaming">
                  <label className="checkbox-label-gaming">
                    <input
                      type="checkbox"
                      name="auto_advance"
                      checked={formData.auto_advance}
                      onChange={handleInputChange}
                      className="checkbox-input-gaming"
                    />
                    <span className="checkmark-gaming"></span>
                    Create the next matches automatically when results come in
                  </label>
                </div>
              )}
            </div>
          </FloatingCard>

          {/* Actions */}
          <FloatingCard className="form-actions-gaming">
            <div className="form-actions-content">
              <button
                type="button"
                onClick={() => navigate("/tournaments")}
                className="floating-btn cancel-btn-gaming"
                disabled={loading}
              >
                <span className="btn-icon">❌</span>
                Cancel
              </button>

              <button
                type="button"
                onClick={handleReset}
                className="floating-btn reset-btn-gaming"
                disabled={loading}
              >
                <span className="btn-icon">🔄</span>
                Reset
              </button>

              <button
                type="submit"
                className="floating-btn create-btn-gaming"
                disabled={loading}
              >
                <span className="btn-icon">{loading ? "⏳" : "⚔️"}</span>
                {loading ? "Creating Tournament..." : "Create Tournament"}
              </button>
            </div>
          </FloatingCard>
        </form>
      </div>
    </GameLayout>
  );
};

export default TournamentCreation;
